/**
 * The journal: everything the player has read this run.
 *
 * Opened automatically when a note is read (`ui:note`) and reachable with J.
 * Like the inventory it may be open while the pointer is still locked, so it
 * has to work from the keyboard alone.
 */

import { add, el } from './dom';
import type { Screen } from './screen';

interface Entry {
  /** Stable authored id, so a re-read is recognised even if a title repeats. */
  id: string;
  title: string;
  body: string;
  el: HTMLElement;
}

export class JournalScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-journal');

  private readonly list = el('nav', 'ui-journal-list');
  private readonly empty = el('p', 'ui-journal-empty', 'Nothing recorded yet.');
  private readonly pageTitle = el('h3', 'ui-journal-title');
  private readonly pageBody = el('p', 'ui-journal-body');
  private readonly entries: Entry[] = [];
  private selected = -1;

  constructor() {
    const head = el('header', 'ui-head');
    add(head, el('h2', 'ui-head-title', 'FIELD NOTES'), el('span', 'ui-head-note', 'THIS RUN'));

    const page = el('article', 'ui-journal-page');
    add(page, this.pageTitle, this.pageBody, this.empty);

    const body = el('div', 'ui-journal-body-wrap');
    add(body, this.list, page);

    const hint = el('footer', 'ui-hint');
    hint.innerHTML = '<span><kbd>&#8593;&#8595;</kbd> browse</span><span><kbd>J</kbd> close</span>';

    add(this.el, head, body, hint);
    this.refresh();
  }

  key(e: KeyboardEvent): boolean {
    if (e.code === 'ArrowDown') {
      this.select(this.selected + 1);
      return true;
    }
    if (e.code === 'ArrowUp') {
      this.select(this.selected - 1);
      return true;
    }
    return false;
  }

  /** Record a note and bring it to the front. A note already read is re-shown. */
  addNote(title: string, body: string, id = title): void {
    const existing = this.entries.findIndex((n) => n.id === id);
    if (existing >= 0) {
      this.select(existing);
      return;
    }
    const item = el('button', 'ui-journal-item');
    item.type = 'button';
    const index = this.entries.length;
    add(
      item,
      el('span', 'ui-journal-num', String(index + 1).padStart(2, '0')),
      el('span', 'ui-journal-item-title', title),
    );
    item.addEventListener('click', () => this.select(index));
    this.list.appendChild(item);
    this.entries.push({ id, title, body, el: item });
    this.select(index);
  }

  /**
   * Fill the journal from a resumed run, without opening it or moving the
   * selection anywhere the player has to close.
   *
   * `addNote` is the reading path: it selects what it adds, because a note that
   * has just been read is the one you want on screen. Replaying eight of those
   * to rebuild a run would be eight overlays. This is the other door.
   */
  restore(notes: readonly { id: string; title: string; body: string }[]): void {
    this.clear();
    for (const n of notes) this.addNote(n.title, n.body, n.id);
    // Back to the top: this is a record being handed back, not a page just read.
    this.select(0);
  }

  clear(): void {
    this.list.replaceChildren();
    this.entries.length = 0;
    this.selected = -1;
    this.refresh();
  }

  private select(index: number): void {
    if (this.entries.length === 0) return;
    this.selected = Math.max(0, Math.min(this.entries.length - 1, index));
    this.refresh();
  }

  private refresh(): void {
    const entry = this.entries[this.selected];
    for (let i = 0; i < this.entries.length; i++) {
      this.entries[i]!.el.classList.toggle('sel', i === this.selected);
    }
    this.empty.hidden = entry !== undefined;
    this.pageTitle.hidden = entry === undefined;
    this.pageBody.hidden = entry === undefined;
    if (!entry) return;
    this.pageTitle.textContent = entry.title;
    this.pageBody.textContent = entry.body;
  }
}
